import { agentsFiltersAtom } from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { AgentListItem } from "@/app/(saas)/(app)/business-brokers/_components/agent-list-item";
import { AgentListItemSkeleton } from "@/app/(saas)/(app)/business-brokers/_components/agent-list-item-skeleton";
import { useNearbyBrokers } from "@/app/(saas)/(app)/business-brokers/_hooks/use-nearby-brokers";
import { useRelaxedBrokers } from "@/app/(saas)/(app)/business-brokers/_hooks/use-relaxed-brokers";
import { Button } from "@/components/ui/button";
import { H3 } from "@/components/ui/heading";
import { valuesWithAppName } from "@/lib/i18n";
import { formatLocation } from "@/lib/locations";
import { useAtom } from "jotai";
import { MapPinIcon, SearchIcon, ArrowRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useMemo } from "react";
import slugify from "slugify";

export type AgentListZeroDataStateProps = {
  countryCode?: string;
};

export function AgentListZeroDataState({
  countryCode,
}: AgentListZeroDataStateProps) {
  const t = useTranslations();
  const [filters, setFilters] = useAtom(agentsFiltersAtom);

  const hasLanguageFilters = filters.languageIds.length > 0;
  const hasSpecialtyFilters = filters.specialtyIds.length > 0;
  const hasNameFilters = !!(filters.name && filters.name.length > 0);
  const hasAnyNonLocationFilters =
    hasLanguageFilters ||
    hasSpecialtyFilters ||
    hasNameFilters;

  const {
    brokers: nearbyBrokers,
    isNearby: hasNearbyBrokers,
    parentLocationName,
    isLoading: isNearbyLoading,
  } = useNearbyBrokers({
    enabled: !hasAnyNonLocationFilters,
    countryCode,
  });

  const {
    brokers: relaxedBrokers,
    isLoading: isRelaxedLoading,
  } = useRelaxedBrokers({
    enabled: hasAnyNonLocationFilters,
    countryCode,
  });

  function clearLanguageFilters() {
    setFilters((prev) => ({ ...prev, languageIds: [] }));
  }

  function clearSpecialtyFilters() {
    setFilters((prev) => ({ ...prev, specialtyIds: [] }));
  }

  function clearNameFilters() {
    setFilters((prev) => ({ ...prev, name: null }));
  }

  function clearAllFilters() {
    setFilters((prev) => ({
      ...prev,
      languageIds: [],
      specialtyIds: [],
      name: null,
    }));
  }

  const regionLinks = useMemo(() => {
    const links: Array<{ label: string; href: string }> = [];
    const location = filters.location;
    if (!location) return links;

    const { country, region, place, neighborhood, district } = location.context;
    const code = country?.code?.toLowerCase() ?? countryCode?.toLowerCase();

    const hasCityOrBelow = !!(place?.name || neighborhood?.name || district?.name);

    if (region && code && hasCityOrBelow) {
      links.push({
        label: t("saas.agentSearch.zds.nearby.exploreRegion", {
          region: region.name,
        }),
        href: `/business-brokers/${code}/${slugify(region.name, { lower: true })}`,
      });
    }

    if (country && code && region) {
      links.push({
        label: t("saas.agentSearch.zds.nearby.exploreCountry", {
          country: country.name,
        }),
        href: `/business-brokers/${code}`,
      });
    }

    return links;
  }, [filters.location, countryCode, t]);

  if (hasAnyNonLocationFilters) {
    return (
      <div className="flex flex-col gap-6">
        <div className="rounded-xl border border-border/80 bg-card/90 p-5 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
          <div className="mb-4 flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-muted p-2">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <H3>{t("saas.agentSearch.zds.title")}</H3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("saas.agentSearch.zds.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {hasLanguageFilters && (
              <Button variant="outline" size="sm" onClick={clearLanguageFilters}>
                {t("saas.agentSearch.zds.clear.languages")}
              </Button>
            )}

            {hasSpecialtyFilters && (
              <Button variant="outline" size="sm" onClick={clearSpecialtyFilters}>
                {t("saas.agentSearch.zds.clear.specialties")}
              </Button>
            )}

            {hasNameFilters && (
              <Button variant="outline" size="sm" onClick={clearNameFilters}>
                {t("saas.agentSearch.zds.clear.name")}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={clearAllFilters}>
              {t("saas.agentSearch.zds.clear.all")}
            </Button>
          </div>
        </div>

        {isRelaxedLoading && (
          <div className="space-y-3">
            <div className="h-5 w-48 animate-pulse rounded bg-muted" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <AgentListItemSkeleton key={`rskel-${i}`} />
              ))}
            </div>
          </div>
        )}

        {!isRelaxedLoading && relaxedBrokers.length > 0 && (
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {t("saas.agentSearch.zds.relaxed.title")}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t("saas.agentSearch.zds.relaxed.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {relaxedBrokers.map((agent) => (
                <AgentListItem key={agent.id} agent={agent} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const locationName = filters.location
    ? formatLocation(filters.location)
    : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-border/80 bg-card/90 p-5 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0 rounded-full bg-muted p-2">
            <MapPinIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <H3>
              {locationName
                ? t("saas.agentSearch.zds.titleNoFilter", {
                    location: locationName,
                  })
                : t("saas.agentSearch.zds.titleNoFilterFallback")}
            </H3>
            <p className="mt-1 text-sm text-muted-foreground">
              {hasNearbyBrokers || isNearbyLoading
                ? t("saas.agentSearch.zds.nearby.subtitle")
                : t("saas.agentSearch.zds.nearby.subtitleNoNearby")}
            </p>
          </div>
        </div>

        {regionLinks.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {regionLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3.5 py-1.5 text-[13px] font-medium text-foreground transition-all duration-200 hover:-translate-y-px hover:border-border hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
              >
                <MapPinIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {link.label}
                <ArrowRightIcon className="h-3 w-3 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {isNearbyLoading && (
        <div className="space-y-3">
          <div className="h-5 w-48 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <AgentListItemSkeleton key={`nskel-${i}`} />
            ))}
          </div>
        </div>
      )}

      {!isNearbyLoading && nearbyBrokers.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card shadow-sm">
              <MapPinIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold tracking-[-0.01em] text-foreground">
              {hasNearbyBrokers
                ? t("saas.agentSearch.zds.nearby.nearbyTitle", {
                    location: parentLocationName ?? "",
                  })
                : t(
                    "saas.agentSearch.zds.nearby.featuredTitle",
                    valuesWithAppName(t, {}),
                  )}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {nearbyBrokers.map((agent) => (
              <AgentListItem key={agent.id} agent={agent} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

AgentListZeroDataState.displayName = "AgentListZeroDataState";
